import { CopyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Card, Input, Table, Tag, Tooltip, Typography, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useCopy } from '../../../hooks/useCopy';
import { get } from '../../../axios/axios';
import { useModal } from '@ebay/nice-modal-react';
import WithdrawModal from '../components/WithdrawModal';
import { useDistributionContext } from '../contexts/DistributionContext';
import './index.scss';

const { Title, Text } = Typography;

// 基础信息
const MyPromotion = () => {
    const copyText = useCopy();
    const withdrawModal = useModal(WithdrawModal);
    const { registerRefresh } = useDistributionContext();

    const [exclusiveCode, setExclusiveCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailList, setDetailList] = useState([]);

    // 佣金数据
    const [commissionData, setCommissionData] = useState({
        totalCommission: 0, // 累积佣金总额
        withdrawnCommission: 0, // 已提现佣金
        availableCommission: 0, // 可提现佣金
        pendingCommission: 0, // 提现中佣金
        commissionRate: 20, // 分成比例
    });

    // 获取佣金信息
    const fetchCommissionInfo = async () => {
        try {
            setLoading(true);
            const userInfo = localStorage.getItem('CommissionUserInfo');
            if (!userInfo) {
                console.error('未找到用户信息');
                return;
            }

            const parsed = JSON.parse(userInfo);
            const token = parsed?.['Commission-Token'];
            if (!token) {
                console.error('未找到token');
                return;
            }

            const res = await get(
                '/member/commission/info',
                {},
                {
                    headers: { 'Commission-Token': token },
                },
            );

            if (res.code === 0) {
                setCommissionData({
                    totalCommission: res.data.totalCommission || 0,
                    withdrawnCommission: res.data.withdrawnCommission || 0,
                    availableCommission: res.data.availableCommission || 0,
                    pendingCommission: res.data.withdrawingCommission || 0, // API返回的是withdrawingCommission
                    commissionRate: res.data.commissionRate || 0,
                });
                // 如果API返回了专属口令，更新状态并保存到localStorage
                if (res.data.exclusiveCode) {
                    setExclusiveCode(res.data.exclusiveCode);
                    // 更新localStorage中的exclusiveCode
                    try {
                        const userInfo = localStorage.getItem('CommissionUserInfo');
                        if (userInfo) {
                            const parsed = JSON.parse(userInfo);
                            parsed.exclusiveCode = res.data.exclusiveCode;
                            localStorage.setItem('CommissionUserInfo', JSON.stringify(parsed));
                        }
                    } catch (error) {
                        console.error('更新localStorage中的exclusiveCode失败:', error);
                    }
                }
            }
        } catch (error) {
            console.error('获取佣金信息失败:', error);
            message.error('获取佣金信息失败，请稍后重试');
        } finally {
            setLoading(false);
        }
    };

    // 获取佣金明细列表
    const fetchDetailList = async () => {
        try {
            setDetailLoading(true);
            const userInfo = localStorage.getItem('CommissionUserInfo');
            if (!userInfo) {
                console.error('未找到用户信息');
                return;
            }

            const parsed = JSON.parse(userInfo);
            const token = parsed?.['Commission-Token'];
            if (!token) {
                console.error('未找到token');
                return;
            }

            const res = await get(
                '/member/commission/detail/list',
                {},
                {
                    headers: { 'Commission-Token': token },
                },
            );
            if (res.code === 0 && Array.isArray(res.data)) {
                setDetailList(res.data);
            }
        } catch (error) {
            console.error('获取佣金明细列表失败:', error);
            message.error('获取佣金明细列表失败，请稍后重试');
        } finally {
            setDetailLoading(false);
        }
    };

    // 从localStorage获取专属口令，并在localStorage变化时更新
    const updateExclusiveCodeFromStorage = () => {
        try {
            const userInfo = localStorage.getItem('CommissionUserInfo');
            if (userInfo) {
                const parsed = JSON.parse(userInfo);
                if (parsed && parsed.exclusiveCode) {
                    setExclusiveCode(parsed.exclusiveCode);
                }
            }
        } catch (error) {
            console.error('解析CommissionUserInfo失败:', error);
        }
    };

    useEffect(() => {
        updateExclusiveCodeFromStorage();
        // 监听localStorage变化（用于登录成功后更新）
        const handleStorageChange = (e) => {
            if (e.key === 'CommissionUserInfo') {
                updateExclusiveCodeFromStorage();
            }
        };
        window.addEventListener('storage', handleStorageChange);
        // 也监听自定义事件（同窗口内的localStorage变化）
        const handleCustomStorageChange = () => {
            updateExclusiveCodeFromStorage();
        };
        window.addEventListener('commissionUserInfoUpdated', handleCustomStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('commissionUserInfoUpdated', handleCustomStorageChange);
        };
    }, []);

    // 获取数据
    useEffect(() => {
        fetchCommissionInfo();
        fetchDetailList();
    }, []);

    // 注册刷新函数
    useEffect(() => {
        const refreshData = () => {
            fetchCommissionInfo();
            fetchDetailList();
        };
        const unregister = registerRefresh('myPromotion', refreshData);
        return unregister;
    }, [registerRefresh]);

    // 提现处理
    const handleWithdraw = () => {
        // 打开提现弹窗
        withdrawModal.show({
            availableCommission: commissionData.availableCommission,
            onSuccess: () => {
                // 提现成功后刷新佣金信息
                fetchCommissionInfo();
                fetchDetailList();
            },
        });
    };

    // 佣金明细表格列配置
    const detailColumns = [
        {
            title: '论文ID',
            dataIndex: 'paperId',
            key: 'paperId',
            width: 120,
        },
        {
            title: '支付金额',
            dataIndex: 'payAmount',
            key: 'payAmount',
            width: 120,
            render: (amount) => `¥${(amount || 0).toFixed(2)}`,
        },
        {
            title: '佣金金额',
            dataIndex: 'commissionAmount',
            key: 'commissionAmount',
            width: 120,
            render: (amount) => `¥${(amount || 0).toFixed(2)}`,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status, record) => {
                const statusMap = {
                    0: { text: record.statusDesc || '待结算', color: 'default' },
                    1: { text: record.statusDesc || '已结算', color: 'success' },
                    2: { text: record.statusDesc || '提现中', color: 'processing' },
                    3: { text: record.statusDesc || '已提现', color: 'success' },
                };
                const statusInfo = statusMap[status] || {
                    text: record.statusDesc || '未知',
                    color: 'default',
                };
                return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
            },
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 180,
        },
    ];

    const dataItems = [
        {
            title: '累积佣金总额(元)',
            value: commissionData.totalCommission,
        },
        {
            title: '已提现佣金(元)',
            value: commissionData.withdrawnCommission,
        },
        {
            title: '可提现佣金(元)',
            value: commissionData.availableCommission,
            showButton: true,
            showTooltip: true,
        },
        {
            title: '提现中佣金(元)',
            value: commissionData.pendingCommission,
        },
        {
            title: '分成比例',
            value: `${commissionData.commissionRate}%`,
            isPercentage: true,
        },
    ];

    return (
        <div className="base-info-container">
            <div className="header-section">
                <Title level={4} style={{ margin: 0 }}>
                    佣金信息
                </Title>
            </div>

            <div className="grid grid-cols-5 gap-4">
                {dataItems.map((item, index) => (
                    <div key={index}>
                        <Card
                            title={<span className="!text-sm !font-normal">{item.title}</span>}
                            extra={
                                <div className="flex items-center gap-2">
                                    {item.showButton && (
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={handleWithdraw}
                                            className="withdraw-btn"
                                        >
                                            提现
                                        </Button>
                                    )}
                                    {item.showTooltip && (
                                        <Tooltip title="可提现金额每日0点更新">
                                            <ExclamationCircleOutlined className="text-primary" />
                                        </Tooltip>
                                    )}
                                </div>
                            }
                            styles={{
                                header: {
                                    borderBottom: 'none',
                                },
                                body: {
                                    paddingTop: '12px',
                                },
                            }}
                        >
                            <div className="flex items-center justify-between">
                                <Text className="text-2xl font-bold" style={{ fontSize: '24px' }}>
                                    {item.isPercentage ? item.value : item.value.toLocaleString()}
                                </Text>
                            </div>
                        </Card>
                    </div>
                ))}
            </div>

            <Card
                title="专属口令"
                styles={{
                    header: {
                        borderBottom: 'none',
                    },
                    body: {
                        paddingTop: '12px',
                    },
                }}
                style={{ marginTop: '16px' }}
            >
                <div className="flex flex-col items-start gap-2">
                    <Input value={exclusiveCode} readOnly />
                    <Button
                        type="primary"
                        icon={<CopyOutlined />}
                        onClick={() => copyText(exclusiveCode)}
                    >
                        复制专属口令
                    </Button>
                </div>
            </Card>

            <Card
                title="佣金明细"
                styles={{
                    header: {
                        borderBottom: 'none',
                    },
                    body: {
                        paddingTop: '12px',
                    },
                }}
                style={{ marginTop: '16px' }}
            >
                <Table
                    columns={detailColumns}
                    dataSource={detailList}
                    loading={detailLoading}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                />
            </Card>
        </div>
    );
};

export default MyPromotion;
